import { Injectable } from '@angular/core';
import { Store } from '@ngrx/store';
import { firstValueFrom, throwError } from 'rxjs';
import { filter, take, timeout } from 'rxjs/operators';
import { ModalControllerService, IModalRef } from '../../../shared/shared-services/overlay/modal-controller.service';
import { SharedModalUtils } from '../../../shared/shared-utils/shared-modal/shared-modal.utils';
import { IconsEnum } from '../../../shared-modules/shared-icons/models/icons.enum';
import { IconColourEnum } from '../../../shared-modules/shared-icons/models/icons-colour.enum';
import { StockTakeFirebaseService } from './stock-take-firebase.service';
import { StockTakeConfirmProcessModalComponent } from '../components/stock-take-confirm-process-modal/stock-take-confirm-process-modal.component';
import { StockTakeRetryLoadingModalComponent } from '../components/stock-take-retry-loading-modal/stock-take-retry-loading-modal.component';
import { ensureStockTakeManagerAccess } from '../utils/stock-take-access.utils';
import { mapFinalFiguresToConfirmModalItems } from '../utils/stock-take-final-figure-display.utils';
import {
  IStockTakeFailedItem,
  parseStockTakeFailedItems,
} from '../utils/stock-take-processing-error.utils';
import { IStockTake, IStockTakeFinalFigure, StockTakeStatus } from '../models';
import * as StockTakeActions from '../store/stock-take.actions';
import * as StockTakeSelectors from '../store/stock-take.selectors';
import { NotificationService } from '../../notifications/service/notification.service';
import { updateNotification } from '../../notifications/store/notification.actions';
import { selectNotificationsByStoreId } from '../../notifications/store/notification.selectors';
import { NotificationTypeEnum } from '../../notifications/models/notification-types.enum';
import { INotification } from '../../notifications/models';
import { path_notification_id } from '../../../shared/shared-services/database-paths';
import { doc, Firestore, updateDoc } from '@angular/fire/firestore';

/**
 * Shared UI flow for re-queuing failed stock take backend processing.
 */
@Injectable({
  providedIn: 'root',
})
export class StockTakeRetryProcessingFlowService {
  constructor(
    private readonly store: Store,
    private readonly modalController: ModalControllerService,
    private readonly sharedModalUtils: SharedModalUtils,
    private readonly firebaseService: StockTakeFirebaseService,
    private readonly notificationService: NotificationService,
    private readonly firestore: Firestore,
  ) {}

  /**
   * Manager-only flow: intro confirmation, loading, final-figures review modal, then re-queue processing.
   * When failed items are provided (or parsed from `errorMsg`), asks to remove them first.
   * On success, marks linked STOCK_TAKE notifications as retried.
   *
   * @returns `true` when retry was dispatched to the server.
   */
  async runRetryFlow(
    storeId: string,
    adjustmentId: string,
    storeAccessCode: number,
    options?: {
      errorMsg?: string | null;
      excludeFailedItems?: IStockTakeFailedItem[];
      excludeItemCodes?: string[];
      itemErrors?: Record<string, unknown> | null;
    },
  ): Promise<boolean> {
    if (!(await ensureStockTakeManagerAccess(this.sharedModalUtils, storeAccessCode, 'finalise'))) {
      return false;
    }

    const excludeFailedItems = this.resolveExcludeFailedItems(options);
    const excludeItemCodes = excludeFailedItems.map((item: IStockTakeFailedItem) => item.code);
    const excludeSet = new Set(excludeItemCodes);
    const isExcludeRetry = excludeSet.size > 0;

    const intro = await this.sharedModalUtils.createModal({
      modalTitleIcon: IconsEnum.infoSlabCircleOutline,
      modalTitleIconColor: IconColourEnum.yellow,
      modalTitle: isExcludeRetry ? 'Retry without failed items' : 'Retry Stock Take Processing',
      contextDescription: isExcludeRetry
        ? this.buildExcludeRetryIntroDescription(excludeFailedItems)
        : [
            'This will send the stock take to the server again for processing.',
            'The same final figures from the last submission will be used.',
            'Do you want to continue?',
          ],
      buttonAcceptText: isExcludeRetry ? 'Remove items & continue' : 'Continue',
      buttonCloseText: 'Cancel',
      buttonAccept: true,
      buttonClose: true,
      buttonCloseCross: false,
      cssClass: ['shared-basic-modal-css-medium'],
    });
    if (intro.data?.buttonPressed !== 'button-accept') {
      return false;
    }

    const loadingModal = await this.modalController.create({
      component: StockTakeRetryLoadingModalComponent,
      componentProps: {
        message: isExcludeRetry ? 'Removing failed items and loading figures…' : 'Loading stock take figures…',
      },
      cssClass: ['shared-basic-modal-css-small', 'stock-take-retry-loading-modal-css'],
      backdropDismiss: false,
    });
    await loadingModal.present();

    let stockTake: IStockTake | null = null;
    let finalFigures: IStockTakeFinalFigure[] = [];
    let loadError: { message: string; title: string } | null = null;

    try {
      try {
        stockTake = await this.firebaseService.getStockTakeOnce(storeId, adjustmentId);
      } catch {
        loadError = {
          title: 'Stock take unavailable',
          message: 'The stock take could not be loaded. Please try again.',
        };
      }

      if (!loadError && !stockTake) {
        loadError = {
          title: 'Stock take not found',
          message: 'This stock take could not be found for the selected store.',
        };
      }

      if (!loadError && stockTake?.status !== StockTakeStatus.failed) {
        loadError = {
          title: 'Cannot retry',
          message: 'Only failed stock takes can be sent to the server again.',
        };
      }

      if (!loadError) {
        try {
          finalFigures = await this.firebaseService.getFinalFiguresOnce(storeId, adjustmentId);
        } catch {
          loadError = {
            title: 'Final figures unavailable',
            message: 'The submitted figures for this stock take could not be loaded.',
          };
        }
      }

      if (!loadError && finalFigures.length === 0) {
        loadError = {
          title: 'No final figures',
          message: 'This stock take has no submitted figures to retry with.',
        };
      }

      if (!loadError && isExcludeRetry) {
        const remainingFigures = finalFigures.filter((figure) => !excludeSet.has(figure.code));
        if (remainingFigures.length === 0) {
          loadError = {
            title: 'Cannot retry without failed items',
            message: 'Removing the rejected items would leave no figures to process.',
          };
        } else {
          try {
            await this.firebaseService.replaceFinalFigures(storeId, adjustmentId, remainingFigures);
            await this.firebaseService.removeStockTakeItemsByCodes(storeId, adjustmentId, excludeItemCodes);
            finalFigures = remainingFigures;
          } catch {
            loadError = {
              title: 'Could not remove failed items',
              message: 'The rejected items could not be removed from this stock take. Please try again.',
            };
          }
        }
      }
    } finally {
      await this.dismissLoadingModal(loadingModal);
    }

    if (loadError) {
      await this.showErrorModal(loadError.title, loadError.message);
      return false;
    }

    if (!stockTake) {
      return false;
    }

    const confirmModal = await this.modalController.create({
      component: StockTakeConfirmProcessModalComponent,
      componentProps: {
        items: mapFinalFiguresToConfirmModalItems(finalFigures),
        stockTake,
        stockTakeName: stockTake.name,
        isRetryMode: true,
      },
      cssClass: 'stock-take-confirm-process-modal',
      backdropDismiss: false,
    });
    await confirmModal.present();

    const { data } = await confirmModal.onDidDismiss();
    const result = data as { confirmed: boolean } | undefined;
    if (!result?.confirmed) {
      return false;
    }

    const processingModal = await this.modalController.create({
      component: StockTakeRetryLoadingModalComponent,
      componentProps: {
        message: 'Re-queuing stock take processing…',
      },
      cssClass: ['shared-basic-modal-css-small', 'stock-take-retry-loading-modal-css'],
      backdropDismiss: false,
    });
    await processingModal.present();

    let didSucceed = false;

    try {
      this.store.dispatch(
        StockTakeActions.retryFailedStockTakeProcessing({ storeId, adjustmentId }),
      );
      const outcome = await firstValueFrom(
        this.store.select(StockTakeSelectors.selectRetryFailedStockTakeProcessingOutcome).pipe(
          filter(
            (result): result is 'success' | 'failure' => result !== null,
          ),
          take(1),
          timeout({
            first: 120_000,
            with: () => throwError(() => new Error('Timeout waiting for stock take retry')),
          }),
        ),
      );
      didSucceed = outcome === 'success';
    } catch {
      didSucceed = false;
    } finally {
      await this.dismissLoadingModal(processingModal);
    }

    if (didSucceed) {
      await this.markLinkedStockTakeNotificationsRetried(storeId, adjustmentId);
    }

    return didSucceed;
  }

  /**
   * Resolves failed items from explicit options or by parsing error text / itemErrors.
   */
  private resolveExcludeFailedItems(options?: {
    errorMsg?: string | null;
    excludeFailedItems?: IStockTakeFailedItem[];
    excludeItemCodes?: string[];
    itemErrors?: Record<string, unknown> | null;
  }): IStockTakeFailedItem[] {
    if (options?.excludeFailedItems && options.excludeFailedItems.length > 0) {
      return options.excludeFailedItems;
    }

    const parsed = parseStockTakeFailedItems(options?.errorMsg, options?.itemErrors);
    if (parsed.length > 0) {
      return parsed;
    }

    const codes = (options?.excludeItemCodes ?? [])
      .map((code) => code.trim())
      .filter((code) => code.length > 0);
    return codes.map((code) => ({ code, reason: 'Rejected by IQ' }));
  }

  /**
   * Builds intro modal copy listing rejected codes and reasons.
   */
  private buildExcludeRetryIntroDescription(failedItems: IStockTakeFailedItem[]): string[] {
    const previewLimit = 12;
    const previewItems = failedItems.slice(0, previewLimit);
    const extraCount = failedItems.length - previewItems.length;
    const itemLines = previewItems.map(
      (item: IStockTakeFailedItem) => `${item.code} — ${item.reason}`,
    );

    return [
      'IQ rejected the following items. They will be removed from this stock take before processing again.',
      '',
      ...itemLines,
      ...(extraCount > 0 ? [`(+${extraCount} more)`] : []),
      '',
      'You will then review the remaining final figures before processing again.',
    ];
  }

  /**
   * Marks failed STOCK_TAKE notifications for this stock take as retried in Firestore and local state.
   * Resolves the notification by event id (stock take id), with a local-store fallback.
   */
  private async markLinkedStockTakeNotificationsRetried(
    storeId: string,
    stockTakeId: string,
  ): Promise<void> {
    try {
      let updatedNotifications = await this.notificationService.markNotificationsRetriedByEventId(
        stockTakeId,
        {
          storeId,
          type: NotificationTypeEnum.STOCK_TAKE,
        },
      );

      if (updatedNotifications.length === 0) {
        updatedNotifications = await this.markRetriedFromLoadedNotifications(storeId, stockTakeId);
      }

      for (const notification of updatedNotifications) {
        this.store.dispatch(updateNotification({ notification }));
      }

      if (updatedNotifications.length === 0) {
        console.warn(
          '[StockTake] No failed STOCK_TAKE notification found to mark as retried for event:',
          stockTakeId,
        );
      }
    } catch (error) {
      console.error(
        '[StockTake] Failed to mark linked STOCK_TAKE notification as retried for event:',
        stockTakeId,
        error,
      );
    }
  }

  /**
   * Fallback: find a matching STOCK_TAKE notification already loaded in NgRx and mark it retried.
   */
  private async markRetriedFromLoadedNotifications(
    storeId: string,
    stockTakeId: string,
  ): Promise<INotification[]> {
    const loaded = await firstValueFrom(this.store.select(selectNotificationsByStoreId(storeId)));
    const matches = (loaded ?? []).filter(
      (notification: INotification) =>
        notification.event === stockTakeId &&
        String(notification.type) === NotificationTypeEnum.STOCK_TAKE &&
        !notification.retried &&
        notification.success !== true &&
        (notification.success as boolean | string | undefined) !== 'true',
    );

    const updated: INotification[] = [];
    for (const notification of matches) {
      if (!notification.notificationId) {
        continue;
      }
      await updateDoc(doc(this.firestore, path_notification_id(notification)), {retried: true});
      updated.push({
        ...notification,
        retried: true,
      });
    }
    return updated;
  }

  private async dismissLoadingModal(loadingModal: IModalRef): Promise<void> {
    await loadingModal.dismiss().catch(() => undefined);
  }

  private async showErrorModal(modalTitle: string, message: string): Promise<void> {
    await this.sharedModalUtils.createModal({
      modalTitleIcon: IconsEnum.alertBox,
      modalTitleIconColor: IconColourEnum.red,
      modalTitle,
      contextDescription: [message],
      buttonAccept: false,
      buttonAcceptText: '',
      buttonClose: true,
      buttonCloseText: 'OK',
      buttonCloseCross: false,
      cssClass: ['shared-basic-modal-css-small'],
    });
  }
}
